import React from 'react'
import ImageGallerySection from '../components/sections/ImageGallerySection'
import { Footer } from '../components/SharedLayout'

const ImageGalleryDemoPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      
      {/* Page Header */}
      <div className="bg-gradient-to-b from-gray-50 to-white py-16 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            🖼️ Image Gallery Section
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed mb-8">
            Horizontal scrolling image gallery with smooth animations and hover effects. 
            Perfect for showcasing portfolio work, lifestyle imagery, or property features.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Smooth Scrolling</h3>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Hidden scrollbars for clean look</li>
                <li>• Touch-friendly mobile scrolling</li>
                <li>• Gradient fade edges</li>
                <li>• Responsive image sizing</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Interactive Effects</h3>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Hover scale animations</li>
                <li>• Category labels on hover</li>
                <li>• Staggered entrance effects</li>
                <li>• Image zoom transitions</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Hudson Club Ready</h3>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Premium lifestyle imagery</li>
                <li>• Brand-consistent styling</li>
                <li>• High-quality Unsplash images</li>
                <li>• Professional portfolio feel</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Main Gallery Showcase */}
      <div className="mb-16">
        <div className="text-center mb-8 px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">✨ Interactive Gallery Experience</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Scroll horizontally through our curated collection of premium home and lifestyle imagery. 
            Hover over images to see category labels and smooth scaling effects.
          </p>
        </div>
        
        <ImageGallerySection />
      </div>

      {/* Technical Features */}
      <div className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">⚡ Technical Features</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            
            {/* Features List */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-3 h-3 rounded-full bg-blue-500 mt-1.5 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Framer Motion Animations</h3>
                  <p className="text-gray-600 text-sm">Smooth entrance animations with staggered timing for each image</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-3 h-3 rounded-full bg-green-500 mt-1.5 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Responsive Design</h3>
                  <p className="text-gray-600 text-sm">Perfect scaling on desktop, tablet, and mobile with touch scrolling</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-3 h-3 rounded-full bg-purple-500 mt-1.5 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Performance Optimized</h3>
                  <p className="text-gray-600 text-sm">Lazy loading images with optimized Unsplash URLs for fast loading</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-3 h-3 rounded-full bg-yellow-500 mt-1.5 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Brand Integration</h3>
                  <p className="text-gray-600 text-sm">Uses exact Figma colors and dimensions for perfect brand consistency</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-3 h-3 rounded-full bg-red-500 mt-1.5 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Cross-browser Compatible</h3>
                  <p className="text-gray-600 text-sm">Hidden scrollbars work across all modern browsers and devices</p>
                </div>
              </div>
            </div>

            {/* Image Categories */}
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">🏠 Image Categories</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-900 text-sm">Interior Design</h4>
                  <p className="text-xs text-gray-600">Living rooms, modern furniture</p>
                </div>
                
                <div className="p-3 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-900 text-sm">Kitchen Design</h4>
                  <p className="text-xs text-gray-600">Marble counters, luxury appliances</p>
                </div>
                
                <div className="p-3 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-900 text-sm">Home Exterior</h4>
                  <p className="text-xs text-gray-600">Landscaping, architectural details</p>
                </div>
                
                <div className="p-3 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-900 text-sm">Bedroom Design</h4>
                  <p className="text-xs text-gray-600">Neutral tones, sophisticated styling</p>
                </div>
                
                <div className="p-3 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-900 text-sm">Home Office</h4>
                  <p className="text-xs text-gray-600">Clean lines, modern workspace</p>
                </div>
                
                <div className="p-3 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-900 text-sm">Bathroom Design</h4>
                  <p className="text-xs text-gray-600">Luxury fixtures, spa-like feel</p>
                </div>
              </div>
            </div>
          </div>

          {/* Integration Code Example */}
          <div className="bg-gray-900 rounded-lg p-8 mb-16">
            <h3 className="text-2xl font-bold text-white mb-6">👨‍💻 Integration Example</h3>
            
            <div className="bg-black/50 rounded-lg p-6 overflow-x-auto">
              <pre className="text-green-400 text-sm">
                <code>{`import ImageGallerySection from '@/components/sections/ImageGallerySection'

// Basic Usage
<ImageGallerySection />

// In your page layout
function HomePage() {
  return (
    <div>
      <HeroSection />
      <ServicesSection />
      <ImageGallerySection />  {/* Portfolio showcase */}
      <TestimonialsSection />
      <Footer />
    </div>
  )
}

// Custom images (modify component)
const customImages = [
  {
    id: 1,
    src: "your-image-url.jpg",
    alt: "Custom image description", 
    category: "Your Category"
  }
  // ... more images
]`}</code>
              </pre>
            </div>
            
            <div className="mt-6 p-4 bg-blue-500/20 rounded-lg border border-blue-500/30">
              <h4 className="font-semibold text-blue-300 mb-2">💡 Pro Tip</h4>
              <p className="text-blue-100 text-sm">
                For best performance, use optimized image URLs with proper dimensions (400x400) 
                and consider implementing a lightbox for full-size viewing.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Perfect for Hudson Club */}
      <div className="py-16 px-4 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">🏠 Perfect for Hudson Club</h2>
          <p className="text-lg text-gray-700 mb-8">
            This image gallery is perfect for showcasing your portfolio of managed properties, 
            before/after transformations, or the luxury lifestyle that Hudson Club enables.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2">Portfolio Showcase</h3>
              <p className="text-gray-600 text-sm">Display your best work and managed properties with professional presentation</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2">Before & After</h3>
              <p className="text-gray-600 text-sm">Show transformation results from your home management services</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2">Lifestyle Imagery</h3>
              <p className="text-gray-600 text-sm">Convey the premium lifestyle experience of effortless homeownership</p>
            </div>
          </div>

          <div className="mt-12 p-8 bg-white rounded-lg shadow-sm">
            <h3 className="text-xl font-bold text-gray-900 mb-4">📊 Design Specifications</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="text-center">
                <div className="font-semibold text-gray-900">Section Height</div>
                <div className="text-gray-600">300px</div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-gray-900">Background</div>
                <div className="text-gray-600">rgb(246,246,241)</div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-gray-900">Image Size</div>
                <div className="text-gray-600">200px × 200px</div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-gray-900">Max Width</div>
                <div className="text-gray-600">1200px</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}

export default ImageGalleryDemoPage 